import { ReactElement, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Component as CaretDownIcon } from 'icons/icon-ui--caret-outline.svg';
import { Component as CaretUpIcon } from 'icons/icon-ui--caret-up-outline.svg';
import { Checkbox, Modal, Select } from '@mantine/core';
import { useFlags } from '@flagsmith/flagsmith/react';
import { Product } from '../../../models/product.types';
import { BreakpointMedia } from '../../../utils/config';
import {
  useFilteredProductReviewsQuery,
  useProductReviewsQuery,
} from '../../../services/product/product.queries';
import { RatingsOverview } from './RatingsOverview';
import { RatingsBreakdown } from './RatingsBreakdown';
import { WriteReviewButton } from './WriteReviewButton';
import { WriteReviewModal } from './WriteReviewModal';
import { ReviewTile } from './ReviewTile';
import { ReviewsPagination } from './ReviewsPagination';
import styles from '../ProductDisplayPage.module.scss';
import { useAuth } from '../../../contexts/AuthContext';

type Props = {
  product?: Product;
  defaultOpen?: boolean;
};

export function CustomerReviews({
  product,
  defaultOpen = true,
}: Props): ReactElement {
  const PER_PAGE = 6;
  const [showReviews, setShowReviews] = useState(defaultOpen);
  const [page, setPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<string | null>('recent');
  const [starFilter, setStarFilter] = useState<string | null>(null);
  const [pictured, setPictured] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [writeReviewOpen, setWriteReviewOpen] = useState(false);
  const isMobile = !useMediaQuery({ query: BreakpointMedia.MIN_MD });
  const isFirstRender = useRef(true);
  const { isLoggedIn } = useAuth();

  const flags = useFlags(['display-customer-review-button']);
  const reviewButtonFlag = flags['display-customer-review-button'];
  const showCustomerReviewButton = reviewButtonFlag.enabled && reviewButtonFlag.value === 'show';

  const toggleShowReviews = () => setShowReviews((prev) => !prev);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    document.getElementById('customer-reviews')
      ?.scrollIntoView({ behavior: 'smooth' });
  }, [page]);

  const sortMap: Record<string, { sort: string; direction: string }> = {
    recent: {
      sort: 'date',
      direction: 'desc',
    },
    highest: {
      sort: 'score',
      direction: 'desc',
    },
    lowest: {
      sort: 'score',
      direction: 'asc',
    },
  };
  const {
    sort,
    direction,
  } = sortMap[sortOrder ?? 'recent'];

  const scores = starFilter ? [Number(starFilter)] : undefined;
  const { data: bottomLineData } = useProductReviewsQuery(product?.id);
  const {
    data: reviewsData,
    isLoading,
  } = useFilteredProductReviewsQuery(
    product?.id, PER_PAGE, page, scores, sort, direction, pictured || undefined,
  );

  const reviews = reviewsData?.reviews
    ?? [].slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const bottomline = bottomLineData?.bottomline;

  const starDistribution = bottomline?.star_distribution;
  const hasReviews = reviews.length > 0;
  const totalReviews = bottomLineData?.pagination.total || 0;
  const totalFilteredReviews = reviewsData?.pagination.total || 0;
  const totalPages = Math.ceil(totalFilteredReviews / PER_PAGE);
  const startIndex = totalFilteredReviews === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const endIndex = Math.min(page * PER_PAGE, totalFilteredReviews);

  return (
    <div className={styles.accordion_container}>
      <div
        role="button"
        tabIndex={0}
        onClick={toggleShowReviews}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleShowReviews();
          }
        }}
      >
        <div className={styles.dropdown}>
          <h3 className={styles.accordion_title}>Customer Reviews</h3>
          {showReviews ? <CaretUpIcon width={23} /> : <CaretDownIcon width={23} />}
        </div>
      </div>
      <hr className={styles.accordion_divider} />
      {showReviews && (
        <div className={styles.reviews_content}>
          <div className={styles.ratings_summary}>
            <RatingsOverview
              bottomline={bottomline}
              hasReviews={(bottomline?.total_review ?? 0) > 0}
            />
            <RatingsBreakdown
              starDistribution={starDistribution}
              totalReviews={totalReviews}
            />
            {isLoggedIn && showCustomerReviewButton
              && <WriteReviewButton onOpen={() => setWriteReviewOpen(true)} />}
          </div>
          {totalReviews > 0 && (
            <>
              <div className={styles.reviews_filters}>
                <div className={styles.sort_filter}>
                  <Select
                    value={starFilter}
                    placeholder="Star Rating"
                    onChange={(val) => {
                      setStarFilter(val);
                      setPage(1);
                    }}
                    data={[
                      {
                        value: '5',
                        label: '5 Stars',
                      },
                      {
                        value: '4',
                        label: '4 Stars',
                      },
                      {
                        value: '3',
                        label: '3 Stars',
                      },
                      {
                        value: '2',
                        label: '2 Stars',
                      },
                      {
                        value: '1',
                        label: '1 Star',
                      },
                    ]}
                    className={styles.reviews_sort}
                    clearable
                    styles={{ input: { cursor: 'pointer' } }}
                  />
                  <Checkbox
                    label="With Media"
                    checked={pictured}
                    onChange={(e) => {
                      setPictured(e.currentTarget.checked);
                      setPage(1);
                    }}
                    className={styles.reviews_pictured_filter}
                    color="dark"
                    styles={{
                      root: {
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                      },
                      label: {
                        display: 'flex',
                        alignItems: 'center',
                        color: '#000',
                      },
                      input: {
                        borderColor: '#000',
                        borderWidth: 2,
                      },
                    }}
                  />
                </div>
                <div className={styles.sort_filter}>
                  Sort by:
                  <Select
                    value={sortOrder}
                    onChange={(val) => {
                      setSortOrder(val);
                      setPage(1);
                    }}
                    data={[
                      {
                        value: 'recent',
                        label: 'Recent Posts',
                      },
                      {
                        value: 'highest',
                        label: 'Rating: High to Low',
                      },
                      {
                        value: 'lowest',
                        label: 'Rating: Low to High',
                      },
                    ]}
                    className={styles.reviews_sort}
                    allowDeselect={false}
                    styles={{ input: { cursor: 'pointer' } }}
                  />
                </div>
              </div>
              {hasReviews ? (
                <>
                  <div className={styles.reviews_grid}>
                    {isMobile ? reviews.map((review) => (
                      <div key={review.id} className={styles.reviews_column}>
                        <ReviewTile
                          review={review}
                          onImageClick={setLightboxImage}
                        />
                      </div>
                    )) : (
                      <>
                        <div className={styles.reviews_column}>
                          {reviews.filter((_, i) => i % 2 === 0)
                            .map((review) => (
                              <ReviewTile
                                key={review.id}
                                review={review}
                                onImageClick={setLightboxImage}
                              />
                            ))}
                        </div>
                        <div className={styles.reviews_column}>
                          {reviews.filter((_, i) => i % 2 !== 0)
                            .map((review) => (
                              <ReviewTile
                                key={review.id}
                                review={review}
                                onImageClick={setLightboxImage}
                              />
                            ))}
                        </div>
                      </>
                    )}
                  </div>
                  <ReviewsPagination
                    page={page}
                    totalPages={totalPages}
                    startIndex={startIndex}
                    endIndex={endIndex}
                    totalReviews={totalFilteredReviews}
                    onPrev={() => setPage((p) => Math.max(1, p - 1))}
                    onNext={() => setPage((p) => Math.min(Math.max(totalPages, 1), p + 1))}
                  />
                </>
              ) : !isLoading && (
                <p>No reviews match the selected filters.</p>
              )}
            </>
          )}
        </div>
      )}
      <WriteReviewModal
        opened={writeReviewOpen}
        onClose={() => setWriteReviewOpen(false)}
        product={product}
      />
      <Modal
        opened={!!lightboxImage}
        onClose={() => setLightboxImage(null)}
        withCloseButton={false}
        centered
        size="auto"
        overlayBlur={6}
        overlayOpacity={0.6}
        styles={{
          body: { padding: 0 },
          modal: {
            background: 'transparent',
            boxShadow: 'none',
          },
        }}
      >
        <button
          type="button"
          onClick={() => setLightboxImage(null)}
          className={styles.lightbox_close}
        >
          ✕
        </button>
        {lightboxImage && (
          <img
            src={lightboxImage}
            alt="Review full size"
            className={styles.lightbox_image}
          />
        )}
      </Modal>
    </div>
  );
}
